import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { enablePushNotifications } from "@/hooks/usePushNotifications";
import { pushSupported, needsIOSInstall, isIOS } from "@/lib/platform";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon, Monitor, Bookmark } from "lucide-react";

// Curated neon line-art portraits, generated once via Gemini Nano Banana and
// served as static assets from /public/avatars/. Keys match backend AVATAR_KEYS.
const AVATAR_KEYS = ["nova", "iris", "milo", "juno", "rex", "lyra", "mox", "zara", "ryo", "vale", "kit"];
const avatarUrl = (key) => `/avatars/${key}.png`;
const PRESET_AVATARS = AVATAR_KEYS.map(avatarUrl);

const TEXT_COLORS = [
  { hex: "#F4F4F0", label: "PAPER" },
  { hex: "#D4FF00", label: "ACID" },
  { hex: "#FF3B30", label: "BLAZE" },
  { hex: "#00E5FF", label: "CYAN" },
  { hex: "#FF3BFF", label: "MAGENTA" },
  { hex: "#7CFF00", label: "LIME" },
];

// Deterministic per-handle pick, mirrors backend `avatar_for`.
const hashCode = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const avatarForHandle = (handle) => avatarUrl(AVATAR_KEYS[hashCode(handle || "wire") % AVATAR_KEYS.length]);

export default function Profile() {
  const { user, setUser } = useAuth();
  const [bio, setBio] = useState(user?.bio || "");
  const [username, setUsername] = useState(user?.username || "");
  const [avatar, setAvatar] = useState(user?.avatar || PRESET_AVATARS[0]);
  const [textColor, setTextColor] = useState(user?.text_color || "#F4F4F0");
  const [arrivalSound, setArrivalSound] = useState(!!user?.arrival_sound);
  const [pushSilent, setPushSilent] = useState(!!user?.push_silent);
  const [gender, setGender] = useState(user?.gender || "");
  const { preference: themePref, effective: themeEff, setTheme } = useTheme();
  const [pushState, setPushState] = useState(() => {
    if (typeof window === "undefined") return "unsupported";
    if (needsIOSInstall()) return "ios-install";
    if (!pushSupported()) return "unsupported";
    if (Notification.permission === "granted") return "on";
    if (Notification.permission === "denied") return "denied";
    return "off";
  });
  const [busy, setBusy] = useState(false);

  const togglePush = async () => {
    if (pushState === "ios-install") {
      toast("Tap the Share icon in Safari, then Add to Home Screen. Open NITEWIRE from the home-screen icon to enable notifications.", { duration: 6000 });
      return;
    }
    if (pushState === "unsupported") {
      toast("Push isn't supported on this device.");
      return;
    }
    if (pushState === "denied") {
      toast(isIOS()
        ? "Notifications are blocked. Open iOS Settings → Notifications → NITEWIRE, then turn Allow Notifications on."
        : "Notifications are blocked in browser settings. Enable them there, then reload.",
        { duration: 6000 });
      return;
    }
    if (pushState === "off") {
      // Fire IMMEDIATELY in the user-gesture stack — do not await anything before this.
      const result = await enablePushNotifications();
      if (result.ok) {
        setPushState("on");
        toast.success("Push notifications on. You'll get pinged for replies and rooms you follow.");
      } else if (result.reason === "denied") {
        setPushState("denied");
        toast.error(isIOS()
          ? "Permission denied. Open iOS Settings → Notifications → NITEWIRE to allow."
          : "Permission denied.");
      } else if (result.reason === "ios-needs-install") {
        setPushState("ios-install");
        toast("Add NITEWIRE to your Home Screen first, then open it from there to enable notifications.", { duration: 6000 });
      } else {
        toast.error("Couldn't enable push. Try again.");
      }
    } else {
      // Turning off — unsubscribe via SW
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const json = sub.toJSON();
          await sub.unsubscribe().catch(() => {});
          await api.delete("/push/subscribe", { data: { endpoint: json.endpoint, keys: json.keys } }).catch(() => {});
        }
        setPushState("off");
        toast("Push notifications off.");
      } catch {
        toast.error("Couldn't disable push.");
      }
    }
  };

  const options = useMemo(() => {
    // Only ever show the curated neon presets in the grid, with the user's
    // handle-seeded default surfaced first. Legacy avatar URLs (e.g. leftover
    // Unsplash portraits from earlier signups) are intentionally hidden.
    const mine = user?.username ? avatarForHandle(user.username) : null;
    const seen = new Set();
    const all = [];
    for (const a of [mine, ...PRESET_AVATARS]) {
      if (a && !seen.has(a)) { seen.add(a); all.push(a); }
    }
    return all;
  }, [user?.username]);

  const reroll = () => {
    const k = AVATAR_KEYS[Math.floor(Math.random() * AVATAR_KEYS.length)];
    setAvatar(avatarUrl(k));
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me", { bio, username, avatar, text_color: textColor, arrival_sound: arrivalSound, push_silent: pushSilent, ...(gender ? { gender } : {}) });
      setUser(data);
      toast.success("SAVED");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-12" data-testid="profile-page">
        <div className="font-mono text-acid text-xs tracking-widest">// PROFILE</div>
        <h1 className="font-heading text-5xl md:text-7xl leading-none mt-2">YOU.</h1>
        <p className="font-mono text-white/60 text-xs mt-3">// {user?.is_guest ? "GUEST" : "MEMBER"} · joined {new Date(user?.created_at).toLocaleDateString()}</p>

        <div className="mt-10 space-y-6">
          <div>
            <div className="flex items-center justify-between">
              <label className="font-mono text-[10px] tracking-widest text-white/60">AVATAR · NEON PORTRAITS</label>
              <button
                type="button"
                onClick={reroll}
                data-testid="btn-reroll-avatar"
                className="font-mono text-[10px] tracking-widest text-acid hover:text-white uppercase border border-acid px-2 py-1"
              >
                Reroll random
              </button>
            </div>
            <div className="mt-3 flex items-center gap-4 border border-white/10 p-4 bg-panel">
              <img src={avatar} alt="" className="w-24 h-24 border-2 border-acid object-cover" data-testid="avatar-preview" />
              <div className="font-mono text-white/60 text-xs">
                <div>&gt; live preview</div>
                <div className="text-white/40">// tap a tile below, paste a URL, or reroll</div>
              </div>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-3">
              {options.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAvatar(a)}
                  data-testid="avatar-choice"
                  className={`aspect-square border-2 ${avatar === a ? "border-acid" : "border-white/10 hover:border-white/40"} overflow-hidden bg-panel`}
                >
                  <img src={a} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <div className="mt-3">
              <label className="font-mono text-[10px] tracking-widest text-white/60">OR PASTE ANY IMAGE URL</label>
              <input
                data-testid="input-avatar-url"
                className="input-brutal mt-1"
                value={avatar}
                onChange={(e) => setAvatar(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/60">HANDLE</label>
            <input data-testid="input-profile-username" className="input-brutal mt-1" value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/60">MESSAGE COLOUR</label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="text-color-picker">
              {TEXT_COLORS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setTextColor(c.hex)}
                  data-testid={`text-color-${c.label.toLowerCase()}`}
                  className={`px-3 py-2 border-2 font-mono text-[11px] tracking-widest ${
                    textColor === c.hex ? "border-acid" : "border-white/15 hover:border-white/40"
                  }`}
                  style={{ color: c.hex }}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="mt-2 border border-white/10 p-3 font-body text-base" style={{ color: textColor }}>
              preview: this is how your words will look in the room.
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/60">BIO — 280 CHARS</label>
            <textarea data-testid="input-profile-bio" className="input-brutal mt-1 min-h-[120px]" value={bio} maxLength={280}
                      onChange={(e) => setBio(e.target.value)} placeholder="who are you when nobody's watching?" />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/60">I AM</label>
            <div className="mt-2 grid grid-cols-2 gap-0" data-testid="profile-gender-select">
              {[
                { value: "male", label: "\u2642 MALE" },
                { value: "female", label: "\u2640 FEMALE" },
              ].map((g) => (
                <button
                  key={g.value}
                  type="button"
                  data-testid={`profile-gender-${g.value}`}
                  onClick={() => setGender(g.value)}
                  className={`font-mono text-xs tracking-widest uppercase py-3 border-2 border-white/20 transition-colors ${
                    gender === g.value
                      ? "bg-acid text-obsidian border-acid"
                      : "text-white/70 hover:text-white hover:border-white/40"
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/60">APPEARANCE</label>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="theme-selector">
              {[
                { key: "light", label: "LIGHT", icon: Sun },
                { key: "dark", label: "DARK", icon: Moon },
                { key: "auto", label: "AUTO", icon: Monitor },
              ].map(({ key, label, icon: Icon }) => {
                const active = themePref === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTheme(key)}
                    data-testid={`theme-${key}`}
                    className={`flex flex-col items-center justify-center gap-1 border-2 p-3 transition-colors ${
                      active
                        ? "border-acid bg-acid/10 text-acid"
                        : "border-white/15 hover:border-white/30 text-white/70"
                    }`}
                    aria-pressed={active}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-mono text-[10px] tracking-widest">{label}</span>
                  </button>
                );
              })}
            </div>
            <div className="font-mono text-[10px] text-white/40 mt-2">
              {themePref === "auto"
                ? `Following your device (currently ${themeEff}). Change your phone's system setting to switch.`
                : `Locked to ${themePref}. Choose AUTO to follow your device.`}
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/60">NOTIFICATIONS</label>
            <button
              type="button"
              onClick={togglePush}
              data-testid="push-toggle"
              className={`mt-2 w-full flex items-center justify-between gap-4 border-2 p-4 text-left transition-colors ${
                pushState === "on" ? "border-acid bg-acid/5"
                : pushState === "denied" ? "border-blaze/60"
                : pushState === "ios-install" ? "border-hotpink"
                : pushState === "unsupported" ? "border-white/10 opacity-50 cursor-not-allowed"
                : "border-white/15 hover:border-white/30"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-heading text-xl leading-none">PUSH NOTIFICATIONS</div>
                <div className="font-mono text-[11px] text-white/60 mt-1" data-testid="push-status-copy">
                  {pushState === "unsupported" && "Not supported on this device."}
                  {pushState === "denied" && (
                    <>Blocked. {isIOS()
                      ? "Open iOS Settings → Notifications → NITEWIRE and turn Allow Notifications on."
                      : "Enable in browser settings then reload."}</>
                  )}
                  {pushState === "ios-install" && (
                    <>Tap <span className="text-hotpink">Share</span> in Safari, then <span className="text-hotpink">Add to Home Screen</span>. Open NITEWIRE from the home-screen icon and come back here to turn on.</>
                  )}
                  {pushState === "off" && "Tap to get pinged for replies to your posts and new activity in rooms you follow."}
                  {pushState === "on" && "You'll get pinged for replies and followed-room activity, even when the app is closed."}
                </div>
              </div>
              {pushState !== "ios-install" && pushState !== "unsupported" && (
                <span
                  aria-hidden
                  className={`shrink-0 w-12 h-6 border-2 relative transition-colors ${
                    pushState === "on" ? "border-acid bg-acid" : "border-white/30 bg-transparent"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-obsidian transition-all ${
                      pushState === "on" ? "left-[calc(100%-1.25rem)]" : "left-0.5"
                    }`}
                  />
                </span>
              )}
            </button>

            <label
              className={`mt-2 flex items-center justify-between gap-4 border-2 p-4 cursor-pointer transition-colors ${
                arrivalSound ? "border-acid bg-acid/5" : "border-white/15 hover:border-white/30"
              }`}
              data-testid="arrival-sound-toggle"
            >
              <div className="min-w-0">
                <div className="font-heading text-xl leading-none">ARRIVAL CHIME</div>
                <div className="font-mono text-[11px] text-white/50 mt-1">
                  Soft chime when someone you follow comes online. Silent for strangers.
                </div>
              </div>
              <span
                className={`shrink-0 w-12 h-6 border-2 relative transition-colors ${
                  arrivalSound ? "border-acid bg-acid" : "border-white/30 bg-transparent"
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 bg-obsidian transition-all ${
                    arrivalSound ? "left-[calc(100%-1.25rem)]" : "left-0.5"
                  }`}
                />
                <input
                  type="checkbox"
                  checked={arrivalSound}
                  onChange={(e) => setArrivalSound(e.target.checked)}
                  className="sr-only"
                  data-testid="arrival-sound-checkbox"
                />
              </span>
            </label>

            <label
              className={`mt-2 flex items-center justify-between gap-4 border-2 p-4 cursor-pointer transition-colors ${
                pushSilent ? "border-cyan_wire bg-cyan_wire/5" : "border-white/15 hover:border-white/30"
              }`}
              data-testid="push-silent-toggle"
            >
              <div className="min-w-0">
                <div className="font-heading text-xl leading-none">SILENT PUSHES</div>
                <div className="font-mono text-[11px] text-white/50 mt-1">
                  Notifications still land in your tray and the badge count still ticks up, but no sound or vibration.
                </div>
              </div>
              <span
                className={`shrink-0 w-12 h-6 border-2 relative transition-colors ${
                  pushSilent ? "border-cyan_wire bg-cyan_wire" : "border-white/30 bg-transparent"
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 bg-obsidian transition-all ${
                    pushSilent ? "left-[calc(100%-1.25rem)]" : "left-0.5"
                  }`}
                />
                <input
                  type="checkbox"
                  checked={pushSilent}
                  onChange={(e) => setPushSilent(e.target.checked)}
                  className="sr-only"
                  data-testid="push-silent-checkbox"
                />
              </span>
            </label>
          </div>
          {!user?.is_guest && (
            <Link
              to="/saved"
              data-testid="link-saved-messages"
              className="flex items-center gap-2 border-2 border-white/15 hover:border-acid hover:text-acid px-4 py-3 font-mono text-xs tracking-widest uppercase text-white/70 transition-colors"
            >
              <Bookmark className="w-4 h-4" /> View Saved Messages
            </Link>
          )}
          <button data-testid="btn-save-profile" className="btn-acid" onClick={save} disabled={busy}>
            {busy ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </main>
    </div>
  );
}
